import React from "react";
import { Localize } from "../../app/lang/langs";
import styles from "../../containers/vresume/VResume.module.css";

/**
 * V-Resume — the live CV preview.
 *
 * Purely presentational: every value arrives as a prop, nothing is fetched, and there is no
 * Redux in this file. It re-renders on each keystroke of the editor, which is the point —
 * the collab sees the document take shape rather than guessing what the export will look
 * like.
 *
 * ── This is NOT the document ──────────────────────────────────────────────────────────
 *
 * The PDF is rendered server-side from `templates/pdf/VResume.html` (see
 * `VResumesController.renderResumeHtml`). This component deliberately mirrors that template
 * — same two-column layout, same section order, same brand colours — but it is a second
 * implementation, and the two have to be changed together. It is a faithful preview, not a
 * guarantee: pagination in particular cannot be shown here, because only the print engine
 * knows where A4 runs out.
 *
 * ── Empty sections are shown, not hidden ──────────────────────────────────────────────
 *
 * The exported PDF drops a section with no content entirely. The preview keeps the heading
 * and shows a muted placeholder instead, because its job while editing is to answer "where
 * will this end up" — a section that vanishes until it has content makes the layout jump
 * around as the form is filled in. `showPlaceholders={false}` gives the export's behaviour
 * for a read-only viewer, who is looking at a finished CV rather than building one.
 */

/**
 * The corporate site the CV's logo links to.
 *
 * Duplicated from `VITAL_WEBSITE` in `api/controllers/VResumesController.js`, which wraps
 * the logo in the same anchor for the exported PDF. Both must point at the same place — the
 * preview promising a link the document does not carry would be worse than no link at all.
 */
export const VITAL_WEBSITE = 'https://www.groupe-vital.com'

const FR_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']

/**
 * The CV's own labels, keyed on `resume.locale`.
 *
 * Deliberately NOT the app's i18n: these belong to the document, whose language is a
 * property of the CV, not of the browser session looking at it. A manager whose UI is in
 * English previewing a French CV must see French headings — otherwise the preview disagrees
 * with the PDF, which reads `vresumes.locale` for exactly the same reason.
 *
 * Kept in sync with FR_LABELS / EN_LABELS in `api/controllers/VResumesController.js`.
 */
const LABELS = {
    fr: {
        summary: 'Profil',
        experience: 'Expérience professionnelle',
        education: 'Formation',
        skills: 'Compétences',
        languages: 'Langues',
        certifications: 'Certifications',
        interests: "Centres d'intérêt",
        availability: 'Disponibilité',
        mobility: 'Mobilité',
        license: 'Permis',
        present: "aujourd'hui",
        levels: {
            native: 'Langue maternelle',
            C2: 'C2 — Maîtrise',
            C1: 'C1 — Avancé',
            B2: 'B2 — Courant',
            B1: 'B1 — Intermédiaire',
            A2: 'A2 — Élémentaire',
            A1: 'A1 — Débutant'
        }
    },
    en: {
        summary: 'Profile',
        experience: 'Professional experience',
        education: 'Education',
        skills: 'Skills',
        languages: 'Languages',
        certifications: 'Certifications',
        interests: 'Interests',
        availability: 'Availability',
        mobility: 'Mobility',
        license: 'Driving licence',
        present: 'present',
        levels: {
            native: 'Native speaker',
            C2: 'C2 — Mastery',
            C1: 'C1 — Advanced',
            B2: 'B2 — Fluent',
            B1: 'B1 — Intermediate',
            A2: 'A2 — Elementary',
            A1: 'A1 — Beginner'
        }
    }
}

export default function VResumePreview({
    resume,
    experiences = [],
    collab,
    /** Absolute URL from NetworkManager.getCollabPhotoUrl(), or null when there is none. */
    photoUrl = null,
    /** The Vital logo, as an <img src>. Optional — the preview shows the wordmark space
     *  empty rather than a broken image when it is absent. */
    logoSrc = null,
    showPlaceholders = true,
    /** UI language, for the placeholder strings only — never for the CV's own labels. */
    lang
}) {

    if (!resume) return null

    const locale = resume.locale === 'en' ? 'en' : 'fr'
    const t = LABELS[locale]
    const months = locale === 'en' ? EN_MONTHS : FR_MONTHS

    /** `mars 2024` — the granularity a CV uses; a day number adds nothing. */
    const monthYear = (value) => {
        if (!value) return ''
        const date = new Date(value)
        if (isNaN(date.getTime())) return ''
        return `${months[date.getMonth()]} ${date.getFullYear()}`
    }

    const period = (experience) => {
        const from = monthYear(experience.start_date)
        if (experience.is_current) return from ? `${from} — ${t.present}` : t.present
        const to = monthYear(experience.end_date)
        if (from && to) return `${from} — ${to}`
        return from || to || ''
    }

    const fullName = [collab && collab.prenom, collab && collab.nom].filter(Boolean).join(' ')
    const initials = [collab && collab.prenom, collab && collab.nom]
        .filter(Boolean).map(part => part.charAt(0).toUpperCase()).join('')

    const included = (experiences || []).filter(experience => experience.included)

    // Driven by the show_* flags — the same flags the PDF reads, so what the collab toggles
    // here is exactly what the client receives.
    const contactParts = []
    if (resume.show_email && collab && collab.email) contactParts.push(collab.email)
    if (resume.show_phone && collab) {
        const phone = collab.mobile_number || collab.telephone_number
        if (phone) contactParts.push(phone)
    }
    if (resume.show_city && collab && collab.city) contactParts.push(collab.city)
    if (resume.show_birth_date && collab && collab.date_naissance) {
        contactParts.push(collab.date_naissance)
    }

    const placeholder = (key) => showPlaceholders
        ? <div className={styles.paperPlaceholder}>{Localize(key, lang)}</div>
        : null

    /** A section, or nothing at all when it is empty and placeholders are off. */
    const section = (title, hasContent, body, emptyKey) => {
        if (!hasContent && !showPlaceholders) return null
        return (
            <div className={styles.paperSection}>
                <div className={styles.paperSectionTitle}>{title}</div>
                {hasContent ? body : placeholder(emptyKey)}
            </div>
        )
    }

    const chips = (items) => (
        <div className={styles.paperChips}>
            {(items || []).map((item, index) => (
                <span className={styles.paperChip} key={`${item}-${index}`}>{item}</span>
            ))}
        </div>
    )

    return (
        <div className={styles.paper}>

            {/* ── Left rail: identity and the short lists ─────────────────── */}
            <div className={styles.paperRail}>

                {/*
                  * Clickable, exactly as in the exported PDF — puppeteer turns the same
                  * anchor into a real PDF link annotation, so what the collab can click here
                  * is what a client can click in the document.
                  *
                  * `rel="noopener noreferrer"` because it opens in a new tab: without it the
                  * opened page gets a `window.opener` handle back into the app.
                  */}
                {resume.show_vital_logo && logoSrc
                    ? <a className={styles.paperLogoLink} href={VITAL_WEBSITE}
                        target="_blank" rel="noopener noreferrer"
                        title={Localize("VRESUME_LOGO_LINK", lang)}>
                        <img className={styles.paperLogo} src={logoSrc} alt="Vital" />
                    </a>
                    : null}

                {resume.show_photo
                    ? (photoUrl
                        ? <img className={styles.paperPhoto} src={photoUrl} alt="" />
                        // A collab with no picture gets initials in the same footprint, so
                        // the layout does not shift if they upload one later. The exported
                        // PDF simply omits the block — there is nothing to shift there.
                        : <div className={styles.paperPhotoEmpty}>{initials || '—'}</div>)
                    : null}

                <div className={styles.paperName}>
                    {fullName || Localize("VRESUME_PREVIEW_NO_NAME", lang)}
                </div>

                {resume.headline
                    ? <div className={styles.paperHeadline}>{resume.headline}</div>
                    : (showPlaceholders
                        ? <div className={styles.paperHeadline} style={{ opacity: 0.45 }}>
                            {Localize("VRESUME_PREVIEW_NO_HEADLINE", lang)}
                        </div>
                        : null)}

                {contactParts.length
                    ? <div className={styles.paperContact}>
                        {contactParts.map((part, index) => <div key={index}>{part}</div>)}
                    </div>
                    : null}

                {resume.availability
                    ? <div className={styles.paperKv} style={{ marginTop: 10 }}>
                        <strong>{t.availability}</strong>{resume.availability}
                    </div>
                    : null}
                {resume.mobility
                    ? <div className={styles.paperKv}>
                        <strong>{t.mobility}</strong>{resume.mobility}
                    </div>
                    : null}
                {resume.driving_license
                    ? <div className={styles.paperKv}>
                        <strong>{t.license}</strong>{resume.driving_license}
                    </div>
                    : null}

                {section(t.education, (resume.education || []).length,
                    <div>
                        {(resume.education || []).map((item, index) => {
                            const years = [item.year_from, item.year_to].filter(Boolean).join(' – ')
                            const school = [item.school, item.city].filter(Boolean).join(', ')
                            return (
                                <div className={styles.paperEdu} key={index}>
                                    {years ? <div className={styles.paperEduYears}>{years}</div> : null}
                                    <div className={styles.paperEduDiploma}>{item.diploma}</div>
                                    {school ? <div className={styles.paperEduSchool}>{school}</div> : null}
                                </div>
                            )
                        })}
                    </div>,
                    "VRESUME_PREVIEW_EMPTY_EDUCATION")}

                {section(t.skills, (resume.skills || []).length,
                    chips(resume.skills), "VRESUME_PREVIEW_EMPTY_SKILLS")}

                {section(t.languages, (resume.languages || []).length,
                    <ul className={styles.paperList}>
                        {(resume.languages || []).map((item, index) => (
                            <li key={index}>
                                <span className={styles.paperListName}>{item.name}</span>
                                {item.level
                                    ? <span className={styles.paperListMeta}>
                                        {t.levels[item.level] || item.level}
                                    </span>
                                    : null}
                            </li>
                        ))}
                    </ul>,
                    "VRESUME_PREVIEW_EMPTY_LANGUAGES")}

                {/* Certifications and interests are optional sections: no placeholder when
                    empty even while editing, because a CV without them is complete. */}
                {(resume.certifications || []).length
                    ? section(t.certifications, true,
                        <ul className={styles.paperList}>
                            {resume.certifications.map((item, index) => {
                                const meta = [item.issuer, item.year].filter(Boolean).join(' · ')
                                return (
                                    <li key={index}>
                                        <span className={styles.paperListName}>{item.name}</span>
                                        {meta ? <span className={styles.paperListMeta}>{meta}</span> : null}
                                    </li>
                                )
                            })}
                        </ul>)
                    : null}

                {(resume.interests || []).length
                    ? section(t.interests, true, chips(resume.interests))
                    : null}
            </div>

            {/* ── Right column: the accroche and the experience ───────────── */}
            <div className={styles.paperMain}>

                {section(t.summary, !!resume.summary,
                    <p className={styles.paperSummary}>{resume.summary}</p>,
                    "VRESUME_PREVIEW_EMPTY_SUMMARY")}

                {section(t.experience, included.length,
                    <div>
                        {included.map(experience => {
                            const org = [experience.client, experience.company]
                                .filter(Boolean).join(' · ')
                            return (
                                <div className={styles.paperXp} key={experience.id}>
                                    <div className={styles.paperXpHead}>
                                        <div className={styles.paperXpTitle}>
                                            {experience.poste || Localize("VRESUME_PREVIEW_NO_POSTE", lang)}
                                        </div>
                                        <div className={styles.paperXpPeriod}>{period(experience)}</div>
                                    </div>
                                    {org || experience.location
                                        ? <div className={styles.paperXpOrg}>
                                            {org}
                                            {experience.location
                                                ? <span className={styles.paperXpLoc}> — {experience.location}</span>
                                                : null}
                                        </div>
                                        : null}
                                    {experience.description
                                        ? <div className={styles.paperXpDesc}>{experience.description}</div>
                                        : null}
                                    {(experience.bullets || []).length
                                        ? <ul className={styles.paperBullets}>
                                            {experience.bullets.map((bullet, index) => (
                                                <li key={index}>{bullet}</li>
                                            ))}
                                        </ul>
                                        : null}
                                </div>
                            )
                        })}
                    </div>,
                    "VRESUME_PREVIEW_EMPTY_EXPERIENCE")}
            </div>
        </div>
    )
}
